import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppleIcon,
  Box,
  ContributionIcon,
  costFormater,
  ReceiptVaultIcon,
  TimerIcon,
} from '@common-fe/common-fe';

import { EMPTY_FIELD_VALUE } from '@/common/constants';
import { IEnrollmentDtoProofOfExpensesTypeEnum } from '@/dtos';
import { CoveragePeriod } from '@/modules/UseMyMoney/UseMyMoney.types';
import { ProofOfExpenseTitle } from '@/modules/UseMyMoney/widgets/AccountDetails/AccountDetails.types';

import AccountsListItemDetails from './AccountsListItemDetails';
import AccountsListItemHeader from './AccountsListItemHeader';
import CoveragePeriodsValues from './CoveragePeriodsValues';
import LastDateToSubmitClaimsValue from './LastDateToSubmitClaimsValue';

interface Props {
  expanded?: boolean;
  accountId?: string;
  enrollmentTitle?: string;
  coveragePeriods?: CoveragePeriod[];
  availableAmount?: number;
  submitClaimsDateEnd?: string;
  categories?: string[];
  proofOfExpensesType?: IEnrollmentDtoProofOfExpensesTypeEnum;
}

const AccountsListItem: React.FC<Props> = ({
  expanded,
  accountId,
  enrollmentTitle,
  coveragePeriods,
  availableAmount,
  submitClaimsDateEnd,
  categories,
  proofOfExpensesType,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(expanded);

  const toggleExpand = useCallback(() => {
    setIsExpanded(!isExpanded);
  }, [isExpanded]);

  const filteredCoveragePeriods = useMemo(
    () =>
      coveragePeriods?.filter((coveragePeriod) =>
        Boolean(coveragePeriod?.coverageDateStart || coveragePeriod?.coverageDateEnd)
      ),
    [coveragePeriods]
  );

  const categoriesCount = useMemo(() => categories?.length || 0, [categories?.length]);

  return (
    <Box width="100%" data-testId={`${accountId}-account-list_item`}>
      <AccountsListItemHeader
        testId={accountId}
        isExpanded={isExpanded}
        onClick={toggleExpand}
        enrollmentTitle={enrollmentTitle}
      />
      {isExpanded ? (
        <Box>
          <AccountsListItemDetails
            testId={`${accountId}-available-account-balance`}
            icon={<ContributionIcon size="16px" color="iconSecondary" />}
            value={costFormater(availableAmount || 0, true)}
            title={t('Available account balance')}
          />
          <AccountsListItemDetails
            testId={`${accountId}-coverage-periods`}
            icon={<TimerIcon size="16px" color="iconSecondary" />}
            value={
              filteredCoveragePeriods?.length ? (
                <CoveragePeriodsValues testId={accountId} coveragePeriods={filteredCoveragePeriods} />
              ) : (
                t('No date restrictions')
              )
            }
            title={t('Date of service covered by this account')}
          />
          <AccountsListItemDetails
            testId={`${accountId}-last-date-to-submit-claims`}
            icon={<TimerIcon size="16px" color="iconSecondary" />}
            value={<LastDateToSubmitClaimsValue testId={accountId} submitClaimsDateEnd={submitClaimsDateEnd} />}
            title={t('Last date to submit claims')}
          />
          <AccountsListItemDetails
            testId={`${accountId}-services`}
            icon={<AppleIcon size="16px" color="iconSecondary" />}
            value={categories?.length ? categories.join(', ') : EMPTY_FIELD_VALUE}
            title={t('Services covered by this account')}
            expandButtonText={`+${categoriesCount > 3 ? categoriesCount - 3 : 0}`}
            expandable
          />
          <AccountsListItemDetails
            testId={`${accountId}-proof-of-expense`}
            icon={<ReceiptVaultIcon size="16px" color="iconSecondary" />}
            value={proofOfExpensesType ? ProofOfExpenseTitle?.[proofOfExpensesType] : EMPTY_FIELD_VALUE}
            title={t('Proof of expense (a receipt, invoice, or EOB)')}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default AccountsListItem;
